import { useToast } from "@/hooks/use-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { useEntryConfigs } from "@/hooks/useFirestore";
import { useRequireSelectedOrgId } from "@/hooks/useRequireSelectedOrgId";
import { db } from "@/lib/firebase";
import { cn } from "@/lib/utils";
import { useCreateTask } from "@/services/dataService";
import { Task } from "@/types/task";
import { doc, Timestamp } from "firebase/firestore";
import { useTraitSelection } from "@/hooks/useTraitSelection";
import { useUnsavedChangesGuard } from "@/hooks/useUnsavedChangesGuard";
import { TraitSelector } from "./TraitSelector";

// Type for task creation data (excluding fields handled by service)
type TaskCreationData = Pick<Task, "name" | "description" | "dataCollectionConfigs" | "state" | "startDate" | "metadata">;

const taskSchema = z.object({
  name: z.string().min(1, { message: "Task name is required" }),
  description: z.string().optional(),
  startDate: z.date(),
  entryConfigData: z.array(z.object({
    entryConfigId: z.string(),
    minNumSamples: z.number().min(1)
  })).optional(),
});

interface AddTaskDialogProps {
  trialId: string;
  experimentId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onTaskAdded?: (task: Task) => void;
  onCreateStart?: (taskName: string, startedAtMs: number) => void;
  onCreateError?: (error: unknown) => void;
  initialValues?: {
    name: string;
    description?: string;
    startDate: Date;
    entryConfigData: Array<{ entryConfigId: string; minNumSamples: number }>;
  };
}

const AddTaskDialog: React.FC<AddTaskDialogProps> = ({
  open,
  onOpenChange,
  trialId,
  experimentId,
  onTaskAdded,
  onCreateStart,
  onCreateError,
  initialValues,
}) => {
  const { userRole } = useAuth();
  const requireOrgId = useRequireSelectedOrgId();
  const { entryConfigs, loading: entryConfigsLoading } = useEntryConfigs();
  const { toast } = useToast();
  const createTask = useCreateTask();

  // Only editors and admins can add new traits (not collectors)
  const canAddTrait = userRole && ["editor", "admin"].includes(userRole);

  const form = useForm<z.infer<typeof taskSchema>>({
    resolver: zodResolver(taskSchema),
    defaultValues: {
      name: "",
      description: "",
      startDate: new Date(),
      entryConfigData: [],
    },
  });

  const selectedEntryConfigData = form.watch("entryConfigData") || [];
  const selectedEntryConfigIds = selectedEntryConfigData.map(d => d.entryConfigId);

  // Warn before discarding an in-progress task (Escape, click-outside, X, Cancel).
  const guard = useUnsavedChangesGuard({ isDirty: form.formState.isDirty, onOpenChange });

  const {
    showAddTraitDialog,
    setShowAddTraitDialog,
    toggleEntryConfig,
    updateMinNumSamples,
    handleAddTrait,
  } = useTraitSelection({ form, selectedEntryConfigData });

  // Reset form when dialog opens with/without initial values
  useEffect(() => {
    if (open) {
      form.reset(initialValues ?? {
        name: "",
        description: "",
        startDate: new Date(),
        entryConfigData: [],
      });
    }
  }, [open, initialValues]);

  const onSubmit = async (data: z.infer<typeof taskSchema>) => {
    const orgId = requireOrgId();
    if (!orgId) return;

    const startedAtMs = Date.now();
    onCreateStart?.(data.name, startedAtMs);

    // Convert entryConfigData to dataCollectionConfigs
    const dataCollectionConfigs = (data.entryConfigData || []).map((configData) => ({
      entryConfigRef: doc(db, `organizations/${orgId}/entryConfigs/${configData.entryConfigId}`),
      minNumSamples: configData.minNumSamples
    }));

    const newTaskData: TaskCreationData = {
      name: data.name,
      description: data.description || "",
      dataCollectionConfigs: dataCollectionConfigs,
      state: "in-progress",
      startDate: Timestamp.fromDate(data.startDate),
      metadata: {},
    };

    // Use the service to create the task
    try {
      const createdTask = await createTask(trialId, experimentId, newTaskData);

      if (createdTask) {
        // Call callback if provided
        if (onTaskAdded) {
          onTaskAdded(createdTask);
        }

        form.reset();
        onOpenChange(false);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to add task",
        variant: "destructive",
      });
      onCreateError?.(error);
    }
  };


  return (
    <>
      <Dialog
        open={open}
        onOpenChange={guard.guardedOnOpenChange}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6">
            <DialogTitle>{initialValues ? "Duplicate Task" : "Add New Task"}</DialogTitle>
            <p className="text-sm text-muted-foreground">
              A task defines which plant traits will be collected in the field.
            </p>
          </DialogHeader>

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="flex flex-col flex-1 overflow-hidden">
              <div className="space-y-4 overflow-y-auto flex-1 px-6">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className={form.formState.errors.name ? "text-destructive" : ""}>
                        Task Name <span className={form.formState.errors.name ? "text-destructive" : "text-muted-foreground"}>(required)</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Enter task name"
                          className={form.formState.errors.name ? "border-destructive focus-visible:ring-destructive" : ""}
                          {...field}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Describe the task details"
                          className="min-h-[80px] resize-none"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="startDate"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel className={form.formState.errors.startDate ? "text-destructive" : ""}>
                        Task Start Date <span className={form.formState.errors.startDate ? "text-destructive" : "text-muted-foreground"}>(required)</span>
                      </FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant="outline"
                              className={cn(
                                "w-full pl-3 text-left font-normal",
                                !field.value && "text-muted-foreground"
                              )}>
                              {field.value ? format(field.value, "PPP") : <span>Select a date</span>}
                              <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent
                          className="w-auto p-0"
                          align="start">
                          <Calendar
                            mode="single"
                            selected={field.value}
                            onSelect={field.onChange}
                            initialFocus
                            className="p-3 pointer-events-auto"
                          />
                        </PopoverContent>
                      </Popover>
                    </FormItem>
                  )}
                />

                <Separator />
                <TraitSelector
                  entryConfigs={entryConfigs}
                  entryConfigsLoading={entryConfigsLoading}
                  selectedEntryConfigData={selectedEntryConfigData}
                  selectedEntryConfigIds={selectedEntryConfigIds}
                  onToggleEntryConfig={toggleEntryConfig}
                  onUpdateMinNumSamples={updateMinNumSamples}
                  showAddTraitDialog={showAddTraitDialog}
                  onShowAddTraitDialogChange={setShowAddTraitDialog}
                  onTraitAdded={handleAddTrait}
                  canAddTrait={canAddTrait}
                />
              </div>

              <DialogFooter className="pt-4 pb-6 px-6 flex-shrink-0">
                <Button
                  type="button"
                  variant="outline"
                  onClick={guard.requestClose}>
                  Cancel
                </Button>
                <Button
                  variant="default"
                  type="submit">
                  {initialValues ? "Create Task" : "Add Task"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {guard.confirmation}
    </>
  );
};

export default AddTaskDialog;
